import React, {useEffect, useMemo, useState} from 'react'
import {KTCard, KTCardBody} from '../../../../_websoft/helpers'
import {useParams, useNavigate} from 'react-router-dom'
import {Table, Form, InputGroup, Button} from 'react-bootstrap'
import {getNaloziZaRolnu, obrisiStavkuSaPripremePaneli} from '../../../api/paneliPromet'
import {PripremaPrometaModel} from '../../../models/PripremaPrometaModel'
import {ToastContainer} from 'react-toastify'
import {prometRolnePaneli} from '../../../api/thermoPrometRolne'
import notifyToast from '../../../components/Toast/Toast'
import {getRolnaDetalji} from '../../../api/rolneDetalji'
import {TableActionButton} from '../../../components/TableActionBtn/TableActionButton'

const PaneliPrometRolna = () => {
    const {id} = useParams<{ id: string }>()
    const navigate = useNavigate()

    const [data, setData] = useState<PripremaPrometaModel[]>([])
    const [total, setTotal] = useState<number>(0)
    const [tezinaRolne, setTezinaRolne] = useState<number>(0)
    const [brojRolne, setBrojRolne] = useState<number>(0)
    const [krajnjaKolicinaRolne, setKrajnjaKolicinaRolne] = useState<string>('')

    const [rolnaDebljina, setRolnaDebljina] = useState('')
    const [rolnaSirina, setRolnaSirina] = useState('')
    const [rolnaBoja, setRolnaBoja] = useState('')
    const [rolnaProizvodjac, setRolnaProizvodjac] = useState('')

    const suggestedKrajnja = useMemo(() => Math.round(tezinaRolne - total), [tezinaRolne, total])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const params = {rolnaId: id}
                const result = await getNaloziZaRolnu(params)

                setData(result.data)

                if (result.data?.length) {
                    setTezinaRolne(result.data[0].repromaterijal.ReprMatTrenutnaKolicina)
                    setBrojRolne(result.data[0].repromaterijal.ReprMatId)
                    calculateTotal(result.data)
                }

                const response = await getRolnaDetalji(Number(id))
                setRolnaDebljina(response.data.ReprMatDebljina ?? '')
                setRolnaSirina(response.data.ReprMatSirina ?? '')
                setRolnaBoja(response.data.boja_lica?.BojaNaziv ?? '')
                setRolnaProizvodjac(response.data.proizvodjac?.PartnerNaziv ?? '')
            } catch (error) {
                console.error('Error fetching data:', error)
                notifyToast(false, 'Greška pri učitavanju podataka.')
            }
        }

        fetchData()
    }, [id])

    const calculateTotal = (rows: any[]) => {
        const t = rows.reduce((acc, item) => acc + parseFloat(item.KolicinaRolnePotrebna), 0)
        setTotal(t)
    }

    const handleKolicinaChange = (index: number, value: string) => {
        const newData = [...data]
        newData[index].KolicinaRolnePotrebna = value
        setData(newData)
        calculateTotal(newData)
    }

    const adjustQuantities = (rows: any[], krajnjaKolicina: number, tezina: number) => {
        const currentTotal = rows.reduce((acc, item) => acc + parseFloat(item.KolicinaRolnePotrebna), 0)
        const difference2 = tezina - krajnjaKolicina
        const difference = difference2 - currentTotal
        const adjustment = difference / rows.length

        return rows.map((item) => ({
            ...item,
            KolicinaRolnePotrebna: (parseFloat(item.KolicinaRolnePotrebna) + adjustment).toFixed(2),
        }))
    }

    const handleKrajnjaKolicinaChange = (e: any) => {
        const value = e.target.value
        setKrajnjaKolicinaRolne(value)

        if (value && data.length) {
            const adjustedData = adjustQuantities(data, parseFloat(value), tezinaRolne)
            setData(adjustedData)
            calculateTotal(adjustedData)
        }
    }

    const handleButtonClick = async () => {
        const additionalData = {
            brojRolne: id,
            krajnjaKolicina: krajnjaKolicinaRolne,
        }

        const payload = data.map((item) => ({
            brojNaloga: item.BrojNaloga,
            stavka: item.Stavka,
            gornjiLim: item.RolnaG,
            KolicinaRolnePotrebna: item.KolicinaRolnePotrebna,
            rolnaSkenirana: item.Skenirano,
            ...additionalData,
        }))

        const response: any = await prometRolnePaneli(payload)
        const responseData = response

        if (responseData.error === 0) {
            notifyToast(true, responseData.message)

            setTimeout(() => {
                navigate('/terminali/termopaneli/promet-rolne', {
                    replace: true,
                    state: {refresh: true},
                })
            }, 3000)
        } else {
            notifyToast(false, responseData.message || 'An unexpected error occurred.')
        }
    }

    const onDelete = (rowId: any) => {
        obrisiStavkuSaPripremePaneli(rowId)
            .then((response) => {
                notifyToast(true, response.data['message'])
                navigate('/terminali/termopaneli/promet-rolne', {state: {refresh: true}})
            })
            .catch((error) => {
                notifyToast(false, error?.data?.['message'] || 'Greška.')
            })
    }

    const onEdit = (rowId: any) => {
        notifyToast(false, 'Izmjena je van upotrebe u modulu!')
    }

    return (
        <KTCard>
            <ToastContainer/>

            <KTCardBody className='py-4'>
                <div className='container-fluid px-0 px-md-3'>
                    {/* Header */}
                    <div
                        className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4'>
                        <div>
                            <h1 className='fw-bolder mb-1'>
                                Promet rolne: <span className='text-success'>{id}</span>
                            </h1>
                            <div className='text-muted'>Popuni krajnju težinu, pa kreiraj promet.</div>
                        </div>

                        <div className='d-flex gap-2'>
                            <Button variant='outline-secondary' className='px-4' onClick={() => navigate(-1)}>
                                Nazad
                            </Button>
                        </div>
                    </div>

                    {/* Info cards */}
                    <div className='row g-3 mb-4'>
                        <div className='col-12 col-md-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body'>
                                    <div className='text-muted small'>Dimenzije</div>
                                    <div className='fs-4 fw-bolder'>
                                        [{rolnaDebljina || '-'} × {rolnaSirina || '-'}]
                                    </div>
                                    <div className='text-muted small mt-2'>ReprMat ID</div>
                                    <div className='fw-semibold'>R-{brojRolne || '-'}</div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-md-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body'>
                                    <div className='text-muted small'>Boja</div>
                                    <div className='fs-4 fw-bolder text-danger'>{rolnaBoja || '-'}</div>
                                    <div className='text-muted small mt-2'>Proizvođač</div>
                                    <div className='fs-5 fw-bolder'>{rolnaProizvodjac || '-'}</div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-md-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body'>
                                    <div className='text-muted fs-2 mt-2'>Trenutna težina</div>
                                    <div className='fw-semibold'>
                                        <span className='text-success fs-2'>{tezinaRolne || 0}</span> kg
                                    </div>

                                    <div className='text-muted fs-2 mt-3'>Prijedlog krajnje težine</div>
                                    <div className='fw-semibold'>
                    <span className='text-danger fs-2'>
                      {Number.isFinite(suggestedKrajnja) ? suggestedKrajnja : 0}
                    </span>{' '}
                                        kg
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Inputs */}
                    <div className='card shadow-sm border-0 mb-4'>
                        <div className='card-body'>
                            <div className='row g-3 align-items-end'>
                                <div className='col-12 col-lg-6'>
                                    <InputGroup>
                                        <InputGroup.Text className='bg-light'>Krajnja težina rolne</InputGroup.Text>
                                        <Form.Control
                                            type='number'
                                            value={krajnjaKolicinaRolne}
                                            onChange={handleKrajnjaKolicinaChange}
                                            placeholder='npr. 150'
                                        />
                                    </InputGroup>
                                    <div className='text-muted small mt-2'>
                                        Total: <span
                                        className='fw-semibold'>{Number.isFinite(total) ? total.toFixed(2) : '0.00'}</span> kg
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className='card shadow-sm border-0'>
                        <div className='card-body'>
                            <div
                                className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 mb-3'>
                                <h3 className='fw-bolder mb-0'>Rolna korištena za izradu naloga</h3>
                                <div className='text-muted small'>Ukupno stavki: {data.length}</div>
                            </div>
                            <div className='table-responsive'>
                                <Table
                                    hover
                                    striped
                                    className='align-middle mb-0 table-rounded table-row-bordered gy-3'
                                >
                                    <thead className='bg-primary text-white'>
                                    <tr>
                                        <th className='px-4 py-3 text-nowrap'>Broj naloga</th>
                                        <th className='px-4 py-3 text-nowrap'>Stavka</th>
                                        <th className='px-4 py-3 text-center text-nowrap'>Gornji</th>
                                        <th className='px-4 py-3 text-center text-nowrap'>Donji</th>
                                        <th className='px-4 py-3 text-nowrap'>Količina</th>
                                        <th className='px-4 py-3 text-center text-nowrap'>Skenirano</th>
                                        <th className='px-4 py-3 text-end text-nowrap'>Akcija</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {data.map((item, index) => (
                                        <tr key={item.Id} className='fw-semibold'>
                                            <td className='px-4 text-nowrap'>{item.BrojNaloga}</td>

                                            <td className='px-4' style={{minWidth: 180}}>
                                                {item.Stavka}
                                            </td>

                                            <td className='text-center'>
                                                {item.RolnaG === 1 ? (
                                                    <i className='bi bi-check-circle-fill fs-4 text-success'/>
                                                ) : (
                                                    <i className='bi bi-x-circle-fill fs-4 text-danger'/>
                                                )}
                                            </td>

                                            <td className='text-center'>
                                                {item.RolnaG === 0 ? (
                                                    <i className='bi bi-check-circle-fill fs-4 text-success'/>
                                                ) : (
                                                    <i className='bi bi-x-circle-fill fs-4 text-danger'/>
                                                )}
                                            </td>

                                            <td style={{minWidth: 140}} className='px-4'>
                                                <Form.Control
                                                    size='sm'
                                                    type='number'
                                                    className='form-control-sm'
                                                    value={item.KolicinaRolnePotrebna}
                                                    onChange={(e) =>
                                                        handleKolicinaChange(index, e.target.value)
                                                    }
                                                />
                                            </td>

                                            <td className='text-center'>
                                                {Number(item.Skenirano) === 1 ? (
                                                    <span className='badge bg-success px-3 py-2'>DA</span>
                                                ) : (
                                                    <span className='badge bg-danger px-3 py-2'>NE</span>
                                                )}
                                            </td>

                                            <td className='text-end px-4'>
                                                <TableActionButton
                                                    onDelete={() => onDelete(item.Id)}
                                                    onEdit={() => onEdit(item.Id)}
                                                />
                                            </td>
                                        </tr>
                                    ))}

                                    {/* TOTAL ROW */}
                                    <tr className='table-light fw-bold'>
                                        <td colSpan={4} className='text-end px-4'>
                                            Total:
                                        </td>

                                        <td className='px-4'>
                                            {Number.isFinite(total) ? total.toFixed(2) : '0.00'}
                                        </td>

                                        <td colSpan={2}></td>
                                    </tr>
                                    </tbody>
                                </Table>
                            </div>
                            <div className='text-muted small mt-3'>Tip: Na mobitelu prevuci tabelu lijevo/desno.</div>
                        </div>
                    </div>

                    {/* Action */}
                    <div className='card shadow-sm border-0 mt-4'>
                        <div
                            className='card-body d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3'>
                            <div className='text-muted'>Unesi krajnju težinu, zatim klikni Kreiraj promet rolne.</div>

                            <div className='ms-md-auto'>
                                <Button
                                    onClick={handleButtonClick}
                                    className='btn btn-success btn-lg px-4'
                                    disabled={!krajnjaKolicinaRolne}
                                >
                                    <i className='bi bi-check2-circle me-2'/>
                                    Kreiraj promet rolne
                                </Button>

                                {!krajnjaKolicinaRolne && (
                                    <div className='text-muted small mt-2'>Obavezno: krajnja težina.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </KTCardBody>
        </KTCard>
    )
}

export default PaneliPrometRolna
